import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { App } from '@df/core/app';
import { getAbsolutePosition } from '@df/dom/fn/get-absolute-position';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AfterContentInitDirectiveService } from '../common/after-content-init.directive';

@Injectable({
    providedIn: 'root'
})
export class AnchorScrollService {
    private subscriptions: Subscription = new Subscription();

    private timeout?: number;

    isAnchorUrl: boolean = false;

    private isInTransition = false;

    private router = inject(Router);
    private scrollToService = inject(ScrollToService);
    private timeoutService = inject(TimeoutService);
    private afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private hashStorageService = inject(HashStorageService);
    private document = inject(DOCUMENT);
    private app = inject(App);

    appInitialize() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const subscription = this.afterContentInitDirectiveService.ready.subscribe(ready => {
            if (ready) {
                this.apply();

                this.subscriptions.add(
                    this.router.events.pipe(filter(e => e instanceof NavigationEnd || e instanceof NavigationStart)).subscribe(e => {
                        if (e instanceof NavigationStart) {
                            if (!this.isInTransition) {
                                this.clear();
                            }
                            this.isAnchorUrl = false;
                        } else if (e instanceof NavigationEnd) {
                            if (this.isInTransition) {
                                this.isInTransition = false;
                            } else {
                                this.apply();
                            }
                        }
                    })
                );

                this.subscriptions.add(
                    this.app.events.on('anchor').subscribe(event => {
                        if (typeof event == 'string') {
                            this.apply(event);
                        }
                    })
                );

                window.addEventListener('hashchange', () => this.onHashChange());

                this.timeoutService.setTimeout(() => subscription.unsubscribe());
            }
        });
    }

    ngOnDestroy(): void {
        if (this.app.isPlatformBrowser) {
            window.removeEventListener('hashchange', () => this.onHashChange());
        }

        this.subscriptions.unsubscribe();
    }

    private onHashChange() {
        const anchor = this.hashStorageService.get('anchor');

        if (anchor) {
            this.apply(anchor);
        }
    }

    private apply(anchor = this.hashStorageService.get('anchor')): void {
        if (anchor && !this.isInTransition) {
            this.isAnchorUrl = true;

            // we try 10 times to get the element and recheck for it to remain on the same position
            let tries = 10;
            let lastPosition: number | undefined;
            let retestedAfterScroll = 5;
            let isFirst = false;

            const doubleCheck = () => {
                if (retestedAfterScroll-- > 0) {
                    // occasionally content gets rendered afetr we scroll (gets into view)
                    // this is double check for that
                    this.timeout = this.timeoutService.setTimeout(() => {
                        const position = this.getPosition(anchor);
                        if (position && lastPosition !== position) {
                            lastPosition = position;
                            this.scrollToService.scrollTo(position);
                        }
                        doubleCheck();
                    }, 500);
                }
            };

            const runTry = (delay = 500) => {
                const cb = () => {
                    const position = this.getPosition(anchor);

                    if (position === undefined || lastPosition !== position) {
                        lastPosition = position;
                        if (tries-- > 0) {
                            if (isFirst) {
                                this.app.events.broadcast('CMS_OUTLET_REFRESH_REQUEST');
                            }
                            runTry();
                            return;
                        }
                    } else {
                        this.scrollToService.scrollTo(position);
                        doubleCheck();
                    }
                };
                if (delay) {
                    this.timeout = this.timeoutService.setTimeout(cb, delay);
                } else {
                    cb();
                }
            };

            this.removeAnchorFromUrl();
            runTry(0);
        }
    }

    private clear() {
        clearTimeout(this.timeout);
    }

    private removeAnchorFromUrl() {
        if (this.hashStorageService.get('anchor')) {
            this.hashStorageService.set('anchor', undefined);
            this.isInTransition = true;
            this.router.navigateByUrl(this.router.url, { skipLocationChange: true });
        }
    }

    private getPosition(anchor: string): number | undefined {
        if (!anchor) {
            return undefined;
        }

        const element =
            this.document.querySelector(`[data-anchor=${anchor}]`) ||
            (anchor.match(/^[\d\w]/) && this.document.querySelector(`#${anchor}`)) ||
            this.document.querySelector(anchor);

        return element ? getAbsolutePosition(element as HTMLElement).y : undefined;
    }
}
